import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import TargetHours from "./TargetHours";
import NotificationSettings from "./NotificationSettings";
import { MapPin } from "lucide-react";

type Props = { companyId: string; isOwner: boolean; onChanged?: () => void };

type Depot = { name: string; start_address: string; end_address: string };

export default function Settings({ companyId, isOwner, onChanged }: Props) {
  const [depots, setDepots] = useState<Depot[]>([]);
  const [catchupWeeks, setCatchupWeeks] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const [crewRes, profileRes, companyRes] = await Promise.all([
      supabase
        .from("crews")
        .select("name, start_address, end_address")
        .eq("company_id", companyId),
      supabase.from("profiles").select("crew").eq("company_id", companyId),
      supabase
        .from("companies")
        .select("missed_catchup_weeks")
        .eq("id", companyId)
        .maybeSingle(),
    ]);

    const names = new Set<string>();
    (crewRes.data ?? []).forEach((c) => c.name && names.add(c.name));
    (profileRes.data ?? []).forEach((p) => {
      const c = (p.crew ?? "").trim();
      if (c && c.toLowerCase() !== "n/a") names.add(c);
    });
    if (names.size === 0) names.add("Crew 1");

    const byName = new Map((crewRes.data ?? []).map((c) => [c.name, c]));
    setDepots(
      Array.from(names)
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({
          name,
          start_address: byName.get(name)?.start_address ?? "",
          end_address: byName.get(name)?.end_address ?? "",
        }))
    );
    setCatchupWeeks(companyRes.data?.missed_catchup_weeks ?? 1);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const saveDepot = async (name: string, field: "start_address" | "end_address", value: string) => {
    const { error } = await supabase
      .from("crews")
      .upsert(
        { company_id: companyId, name, [field]: value.trim() || null },
        { onConflict: "company_id,name" }
      );
    if (error) {
      toast.error("Could not save address");
      return;
    }
    toast.success(`${name} ${field === "start_address" ? "start" : "end"} address saved`);
    onChanged?.();
  };

  const saveCatchup = async (weeks: number) => {
    setCatchupWeeks(weeks);
    const { error } = await supabase
      .from("companies")
      .update({ missed_catchup_weeks: weeks })
      .eq("id", companyId);
    if (error) toast.error("Could not save catch-up window");
    else onChanged?.();
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-display text-lg font-semibold">Settings</h2>
        <p className="text-sm text-muted-foreground">
          Everything that shapes how your schedule, routes, and customer messages behave.
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <MapPin className="h-4 w-4" />
              Crew depots
            </CardTitle>
            <CardDescription>
              Where each crew starts and ends the day. Routes are optimized from these points.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : (
              depots.map((d) => (
                <div key={d.name} className="space-y-2 rounded-lg border p-3">
                  <p className="text-sm font-semibold">{d.name}</p>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Start address</Label>
                      <Input
                        className="h-8 text-xs"
                        placeholder="Shop, yard, etc."
                        disabled={!isOwner}
                        value={d.start_address}
                        onChange={(e) =>
                          setDepots((prev) =>
                            prev.map((x) =>
                              x.name === d.name ? { ...x, start_address: e.target.value } : x
                            )
                          )
                        }
                        onBlur={(e) => saveDepot(d.name, "start_address", e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">End address</Label>
                      <Input
                        className="h-8 text-xs"
                        placeholder="Defaults to start"
                        disabled={!isOwner}
                        value={d.end_address}
                        onChange={(e) =>
                          setDepots((prev) =>
                            prev.map((x) =>
                              x.name === d.name ? { ...x, end_address: e.target.value } : x
                            )
                          )
                        }
                        onBlur={(e) => saveDepot(d.name, "end_address", e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Work day targets</CardTitle>
            <CardDescription>
              Used as the target line in workload analysis and to balance crews.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <TargetHours companyId={companyId} isOwner={isOwner} onChanged={onChanged} embedded />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Missed service handling</CardTitle>
            <CardDescription>
              How long a missed customer stays eligible for an automatic catch-up stop.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex items-center gap-3">
            <Label className="text-sm">Auto catch-up window</Label>
            <Select
              value={String(catchupWeeks)}
              disabled={!isOwner}
              onValueChange={(v) => saveCatchup(Number(v))}
            >
              <SelectTrigger className="h-8 w-40 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Off</SelectItem>
                <SelectItem value="1">1 week</SelectItem>
                <SelectItem value="2">2 weeks</SelectItem>
                <SelectItem value="3">3 weeks</SelectItem>
                <SelectItem value="4">4 weeks</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        <NotificationSettings companyId={companyId} isOwner={isOwner} />
      </div>
    </div>
  );
}
